import React, { useEffect, useState } from 'react';
import { teamAPI } from '../lib/api';
import { usePermissions } from '../contexts/PermissionContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Plus, Search, UserPlus } from 'lucide-react';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { format } from 'date-fns';
import InviteTeamDialog from '../components/dashboard/InviteTeamDialog';

export default function Team() {
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission('hr', 'users', 'create');
  
  const [members, setMembers] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, [search]);

  const loadData = async () => {
    try {
      const [membersRes, attendanceRes] = await Promise.all([
        teamAPI.getMembers({ search }),
        teamAPI.getAttendance({})
      ]);
      setMembers(membersRes.data);
      setAttendance(attendanceRes.data);
    } catch (error) {
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="team-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Team</h1>
          <p className="text-sm text-muted-foreground">Manage team members and attendance</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setInviteOpen(true)} data-testid="invite-member-button">
            <UserPlus className="mr-2 h-4 w-4" />
            Invite Member
          </Button>
          {canCreate && (
            <Button data-testid="create-member-button">
              <Plus className="mr-2 h-4 w-4" />
              Add Member
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="members" className="space-y-4" data-testid="team-tabs">
        <TabsList>
          <TabsTrigger value="members" data-testid="tab-members">Members</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="tab-attendance">Attendance</TabsTrigger>
        </TabsList>

        <TabsContent value="members" className="space-y-4">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search members..."
              className="pl-8"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="members-search-input"
            />
          </div>
          <div className="rounded-md border" data-testid="members-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center">Loading...</TableCell>
                  </TableRow>
                ) : members.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center" data-testid="no-members-message">No team members found</TableCell>
                  </TableRow>
                ) : (
                  members.map((member) => (
                    <TableRow key={member.id} data-testid={`member-row-${member.id}`}>
                      <TableCell className="font-medium">{member.name}</TableCell>
                      <TableCell>{member.email || '-'}</TableCell>
                      <TableCell>{member.phone || '-'}</TableCell>
                      <TableCell>{member.role}</TableCell>
                      <TableCell>{member.department || '-'}</TableCell>
                      <TableCell>
                        <Badge variant={member.is_active ? 'default' : 'secondary'} data-testid={`member-status-${member.id}`}>
                          {member.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="attendance" className="space-y-4">
          <div className="rounded-md border" data-testid="attendance-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Member</TableHead>
                  <TableHead>Clock In</TableHead>
                  <TableHead>Clock Out</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center">Loading...</TableCell>
                  </TableRow>
                ) : attendance.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center" data-testid="no-attendance-message">No attendance records found</TableCell>
                  </TableRow>
                ) : (
                  attendance.map((record) => (
                    <TableRow key={record.id} data-testid={`attendance-row-${record.id}`}>
                      <TableCell>{format(new Date(record.attendance_date), 'MMM dd, yyyy')}</TableCell>
                      <TableCell>{record.member_id}</TableCell>
                      <TableCell>{record.clock_in || '-'}</TableCell>
                      <TableCell>{record.clock_out || '-'}</TableCell>
                      <TableCell>
                        <Badge data-testid={`attendance-status-${record.id}`}>{record.status}</Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <InviteTeamDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        onInvited={loadData}
      />
    </div>
  );
}
